/**
 * Экономика адъювантной терапии HER2+ РМЖ.
 *
 * Расчёт повторяет лист «Аудит-Перьета» из «Расчеты к отчету.xlsx»:
 *
 *   контингент на лечении рецидива:  cont[0] = новые рецидивы года,
 *                                    cont[k] = cont[k-1] прошлого года × survival[k-1]
 *   стоимость лечения рецидивов:     Σ cont[k] × recurrenceCostByAge[k]
 *   стоимость адъювантной терапии:   Σ пациенты × доля режима × цена случая
 *   выгода сценария:                 экономия на рецидивах − доплата за терапию
 *
 * Числа рецидивов исходная модель не выводит из кривых выживаемости — они
 * приходят готовыми рядами для трёх сценариев. Для промежуточной доли
 * пертузумаба линейно интерполируем между рядом «без пертузумаба» и рядом
 * сценария внедрения (его доля по годам — plan.perjeta).
 */

/** Число рецидивов в год при заданной доле пертузумаба. */
export const recurrencesAtShare = (economics, yearIndex, share) => {
    const {recurrences, plan} = economics
    const base = recurrences.withoutPerjeta[yearIndex]
    const target = recurrences.withPerjeta[yearIndex]
    const planShare = plan.perjeta[yearIndex]

    if (!planShare) return base
    return base + (target - base) * (share / planShare)
}

/**
 * Доли режимов при заданной доле пертузумаба.
 *
 * Интерполируем между сценарием без пертузумаба (base) и сценарием внедрения
 * (plan) тем же коэффициентом, что и рецидивы: иначе при нулевой доле
 * распределение трастузумаб/химиотерапия не совпадает с таблицей сравнения.
 */
export const regimenShares = (economics, yearIndex, share) => {
    const {plan, base} = economics
    const planShare = plan.perjeta[yearIndex]
    const k = planShare ? share / planShare : 0

    const baseTrastuzumab = base ? base.trastuzumab[yearIndex] : plan.trastuzumab[yearIndex]
    const baseChemo = base ? base.chemo[yearIndex] : plan.chemo[yearIndex]

    const perjeta = Math.max(share, 0)
    const trastuzumab = baseTrastuzumab + (plan.trastuzumab[yearIndex] - baseTrastuzumab) * k
    const chemo = baseChemo + (plan.chemo[yearIndex] - baseChemo) * k

    // нормируем: сумма долей всегда единица
    const rest = Math.max(1 - perjeta, 0)
    const restSum = trastuzumab + chemo
    if (restSum <= 0) return {perjeta, trastuzumab: rest, chemo: 0}

    return {
        perjeta,
        trastuzumab: rest * (trastuzumab / restSum),
        chemo: rest * (chemo / restSum),
    }
}

/**
 * Ряд показателей для одного сценария.
 *
 * @param cohorts  [{year, patients}] — пациенты на адъювантной терапии по годам
 * @param share    доля пертузумаба (0..1) либо функция (yearIndex) => доля
 */
export const scenarioSeries = (economics, cohorts, share) => {
    const {survival, recurrenceCostByAge, schemePrice} = economics
    const shareAt = typeof share === 'function' ? share : () => share

    // контингент на лечении рецидива по годам после его наступления
    const living = []          // living[k][i]
    const result = []

    cohorts.forEach((cohort, i) => {
        const yearIndex = economics.years.indexOf(cohort.year)
        if (yearIndex < 0) {
            result.push(null)
            return
        }

        const currentShare = shareAt(yearIndex)
        const shares = regimenShares(economics, yearIndex, currentShare)
        const newRecurrences = recurrencesAtShare(economics, yearIndex, currentShare)

        living[0] = living[0] || []
        living[0][i] = newRecurrences
        for (let k = 1; k < recurrenceCostByAge.length; k++) {
            living[k] = living[k] || []
            const previous = (living[k - 1] || [])[i - 1] || 0
            living[k][i] = previous * (survival[k - 1] || 0)
        }

        let onTreatment = 0
        let recurrenceCost = 0
        for (let k = 0; k < recurrenceCostByAge.length; k++) {
            const value = living[k][i] || 0
            onTreatment += value
            recurrenceCost += value * recurrenceCostByAge[k]
        }

        const adjuvantCost = cohort.patients * (
            shares.perjeta * schemePrice.perjeta
            + shares.trastuzumab * schemePrice.trastuzumab
            + shares.chemo * schemePrice.chemo)

        result.push({
            year: cohort.year,
            patients: cohort.patients,
            shares,
            newRecurrences,
            onTreatment,
            adjuvantCost,
            recurrenceCost,
        })
    })

    return result
}

/**
 * Сравнение сценариев А и В.
 * Возвращает ряды в млн ₽ и в пациентах, пригодные для графиков.
 */
export const compareStrategies = (resultData, economics, strategy) => {
    if (!economics?.recurrences || !economics?.years) return []

    const cohorts = resultData
        .filter(item => economics.years.includes(item.year))
        .map(item => ({year: item.year, patients: Number(item[economics.cohortStep]) || 0}))

    const shareOf = suffix => Number(strategy[economics.perjetaShareVar + suffix]) / 100 || 0
    const a = scenarioSeries(economics, cohorts, shareOf('A'))
    const b = scenarioSeries(economics, cohorts, shareOf('B'))

    let cumulativeNet = 0
    let cumulativeExtra = 0
    let cumulativeAvoided = 0
    return a.map((itemA, i) => {
        const itemB = b[i]
        const saved = itemA.recurrenceCost - itemB.recurrenceCost
        const extra = itemB.adjuvantCost - itemA.adjuvantCost
        const net = saved - extra
        const avoided = itemA.newRecurrences - itemB.newRecurrences
        cumulativeNet += net
        cumulativeExtra += extra
        cumulativeAvoided += avoided

        // «Цена предотвращённого рецидива» из исходной модели: во сколько
        // обходится каждый рецидив, которого удалось избежать (накопленным итогом)

        return {
            year: itemA.year,
            recurrencesA: Math.round(itemA.newRecurrences),
            recurrencesB: Math.round(itemB.newRecurrences),
            onTreatmentA: Math.round(itemA.onTreatment),
            onTreatmentB: Math.round(itemB.onTreatment),
            avoided: Math.round(avoided),
            costPerAvoided: cumulativeAvoided > 0
                ? Math.round(cumulativeExtra / cumulativeAvoided / 1e6 * 10) / 10
                : null,
            saved: Math.round(saved / 1e6),
            extra: Math.round(extra / 1e6),
            net: Math.round(net / 1e6),
            cumulativeNet: Math.round(cumulativeNet / 1e6),
        }
    })
}
